import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import {
  IconEdit,
  IconTrash,
  IconUserPlus,
  IconUsers,
} from '@/components/icons'
import {
  ActionButton,
  AppModal,
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  PageCenterLoader,
  PageShell,
  PageToolbar,
} from '@/components/ui'
import { ShiftPresenceDot } from '@/components/ui/ShiftPresenceDot'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import { isRoleListedInStaffPickers, workspaceRoleLabel } from '@/lib/operator-role-labels'
import {
  mergeWithDefaultPermissions,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import { resolveOperatorAvatarSrc } from '@/lib/dashboard-ai-operator'
import { formatDateTimeDot } from '@/lib/ticket-display'
import type { MyPermissionsResponse, RolePermissionItem, WorkspaceUserRow } from '@/types/api'

function operatorName(o: WorkspaceUserRow): string {
  const full = [o.first_name, o.last_name].filter(Boolean).join(' ').trim()
  return full || o.email || '—'
}

function operatorInitials(o: WorkspaceUserRow): string {
  const a = o.first_name?.[0] || o.last_name?.[0] || o.email?.[0] || '?'
  return a.toUpperCase()
}

const ROLE_PRIORITY: Record<string, number> = { admin: 0, operator: 1, viewer: 2 }

function sortRoleItems(items: RolePermissionItem[]): RolePermissionItem[] {
  return [...items]
    .filter((r) => r && r.is_enabled !== false && !r.is_deleted && r.role)
    .filter((r) => isRoleListedInStaffPickers(r.role, { includeOwner: false }))
    .sort((a, b) => {
      const ra = String(a.role || '').toLowerCase()
      const rb = String(b.role || '').toLowerCase()
      const pa = Object.prototype.hasOwnProperty.call(ROLE_PRIORITY, ra) ? ROLE_PRIORITY[ra] : 99
      const pb = Object.prototype.hasOwnProperty.call(ROLE_PRIORITY, rb) ? ROLE_PRIORITY[rb] : 99
      if (pa !== pb) return pa - pb
      const an = String(a.display_name || workspaceRoleLabel(ra))
      const bn = String(b.display_name || workspaceRoleLabel(rb))
      return an.localeCompare(bn, 'ru')
    })
}

export function OperatorsPage() {
  const { user } = useAuth()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])
  const effectivePerm = mergeWithDefaultPermissions(perm, user?.role)
  const canOperators = hasPermission(effectivePerm, 'access_operators')
  const canManage = hasPermission(effectivePerm, 'manage_projects')
  const canManageEmails = hasPermission(effectivePerm, 'manage_operator_emails')
  const canEditOperatorRecord = canManage || canManageEmails

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [operators, setOperators] = useState<WorkspaceUserRow[]>([])
  const [roleMatrix, setRoleMatrix] = useState<RolePermissionItem[]>([])

  const [search, setSearch] = useState('')
  const [sortMode, setSortMode] = useState<'name_asc' | 'name_desc'>('name_asc')
  const [onlyOnline, setOnlyOnline] = useState(false)
  const [onlyActive, setOnlyActive] = useState(true)
  const [showInactive, setShowInactive] = useState(false)
  const [onlyAdminOp, setOnlyAdminOp] = useState(false)
  const [roleFilter, setRoleFilter] = useState<string[]>([])

  const [formOpen, setFormOpen] = useState(false)
  const [editOp, setEditOp] = useState<WorkspaceUserRow | null>(null)
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [passwordNew, setPasswordNew] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [workspaceRole, setWorkspaceRole] = useState('operator')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  const [profileOpen, setProfileOpen] = useState(false)
  const [profileOp, setProfileOp] = useState<WorkspaceUserRow | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [users, roles] = await Promise.all([
        api.workspaceUsers({ includeInactive: showInactive }),
        api.workspaceRolePermissions().catch(() => ({ items: [] as RolePermissionItem[] })),
      ])
      setOperators(Array.isArray(users) ? users : [])
      setRoleMatrix(sortRoleItems(roles.items ?? []))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
      setOperators([])
    } finally {
      setLoading(false)
    }
  }, [showInactive])

  useEffect(() => {
    void load()
  }, [load])

  const customRoleMap = useMemo(() => {
    const m = new Map<string, RolePermissionItem>()
    for (const r of roleMatrix) {
      if (r?.is_custom && r.role) m.set(String(r.role).toLowerCase(), r)
    }
    return m
  }, [roleMatrix])

  const roleOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const o of operators) {
      const key = String(o.custom_role || o.role || '').toLowerCase()
      if (!key) continue
      if (!map.has(key)) {
        const label = customRoleMap.get(key)?.display_name || workspaceRoleLabel(key)
        map.set(key, label)
      }
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
  }, [operators, customRoleMap])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const roleSet = roleFilter.length ? new Set(roleFilter.map((r) => r.toLowerCase())) : null
    let list = operators.filter((o) => {
      if (o.is_deleted) return false
      if (String(o.email || '').toLowerCase().includes('.deleted.')) return false
      if (onlyOnline && !o.is_online) return false
      if (onlyActive && o.is_active === false) return false
      if (onlyAdminOp && !['admin', 'operator'].includes(String(o.role || '').toLowerCase())) return false
      if (roleSet) {
        const effectiveRole = String(o.custom_role || o.role || '').toLowerCase()
        if (!roleSet.has(effectiveRole)) return false
      }
      if (!q) return true
      const name = operatorName(o).toLowerCase()
      const em = String(o.email || '').toLowerCase()
      const un = String(o.username || '').toLowerCase()
      const mm = String(o.mattermost_login || '').toLowerCase()
      return name.includes(q) || em.includes(q) || un.includes(q) || mm.includes(q)
    })
    list = [...list].sort((a, b) => {
      const cmp = operatorName(a).localeCompare(operatorName(b), 'ru', { sensitivity: 'base' })
      return sortMode === 'name_desc' ? -cmp : cmp
    })
    return list
  }, [operators, search, sortMode, onlyOnline, onlyActive, onlyAdminOp, roleFilter])

  const openCreate = () => {
    setEditOp(null)
    setEmail('')
    setUsername('')
    setPassword('')
    setPasswordNew('')
    setFirstName('')
    setLastName('')
    setWorkspaceRole('operator')
    setIsActive(true)
    const items = sortRoleItems(roleMatrix)
    if (items.length) setWorkspaceRole(String(items[0].role || 'operator').toLowerCase())
    setFormOpen(true)
  }

  const openEdit = (o: WorkspaceUserRow) => {
    setEditOp(o)
    setEmail(o.email || '')
    setUsername(o.username || '')
    setPassword('')
    setPasswordNew('')
    setFirstName(o.first_name || '')
    setLastName(o.last_name || '')
    const key = String(o.custom_role || o.role || 'operator').toLowerCase()
    setWorkspaceRole(key)
    setIsActive(o.is_active !== false)
    setFormOpen(true)
  }

  const openProfile = (o: WorkspaceUserRow) => {
    setProfileOp(o)
    setProfileOpen(true)
  }

  const roleByKey = useMemo(() => {
    const m = new Map<string, RolePermissionItem>()
    for (const r of roleMatrix) {
      if (r.role) m.set(String(r.role).toLowerCase(), r)
    }
    return m
  }, [roleMatrix])

  const submitForm = async () => {
    if (editOp && canManageEmails && !canManage) {
      const em = email.trim()
      const un = username.trim()
      if (!em) {
        showNotification({ title: 'Форма', message: 'Укажите email.', color: 'yellow' })
        return
      }
      const emailChanged = em !== (editOp.email || '').trim()
      const usernameChanged = un !== (editOp.username || '').trim()
      if (!emailChanged && !usernameChanged) {
        showNotification({
          title: 'Без изменений',
          message: 'Email и логин совпадают с текущими.',
          color: 'yellow',
        })
        return
      }
      const payload: Record<string, unknown> = {}
      if (emailChanged) payload.email = em
      if (usernameChanged) payload.username = un || null
      setSaving(true)
      try {
        await api.userUpdate(editOp.id, payload)
        setFormOpen(false)
        void load()
        showNotification({ title: 'Сохранено', message: '', color: 'green' })
      } catch (e) {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
          color: 'red',
        })
      } finally {
        setSaving(false)
      }
      return
    }

    const selected = String(workspaceRole || '').toLowerCase()
    const cfg = roleByKey.get(selected)
    const body: Record<string, unknown> = {
      first_name: firstName.trim() || null,
      last_name: lastName.trim() || null,
      is_active: isActive,
    }
    if (cfg?.is_custom) {
      body.custom_role = selected
      body.role = String(cfg.base_role || 'operator').toLowerCase()
    } else if (selected) {
      body.role = selected
      body.custom_role = null
    }
    if (editOp) {
      const pw = passwordNew.trim()
      if (pw) body.password = pw
      if (canManage || canManageEmails) {
        const em = email.trim()
        if (em) body.email = em
        body.username = username.trim() || null
      }
    } else {
      body.email = email.trim()
      const un = username.trim()
      if (un) body.username = un
      body.password = password
    }
    setSaving(true)
    try {
      if (editOp) {
        await api.userUpdate(editOp.id, body)
      } else {
        if (!body.email || !body.password) {
          showNotification({ title: 'Форма', message: 'Укажите email и пароль.', color: 'yellow' })
          setSaving(false)
          return
        }
        await api.userCreate(body)
      }
      setFormOpen(false)
      void load()
      showNotification({ title: 'Сохранено', message: '', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = (o: WorkspaceUserRow) => {
    const n = operatorName(o)
    openConfirmModal({
      title: 'Удалить оператора?',
      children: (
        <Text size="sm">
          Удалить «{n}»? Все назначенные на него обращения станут без исполнителя.
        </Text>
      ),
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.userDelete(o.id)
          void load()
          showNotification({ title: 'Удалено', message: '', color: 'green' })
        } catch (e) {
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Не удалось удалить',
            color: 'red',
          })
        }
      },
    })
  }

  if (!canOperators) {
    return (
      <PageShell>
        <Alert color="gray" variant="light" title="Нет доступа">
          Недостаточно прав для раздела «Операторы».
        </Alert>
      </PageShell>
    )
  }

  const profileCustom =
    profileOp?.custom_role && customRoleMap.has(String(profileOp.custom_role).toLowerCase())
      ? customRoleMap.get(String(profileOp.custom_role).toLowerCase())?.display_name ||
        profileOp.custom_role
      : null

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          leading={
            <Box>
              <Group gap="xs" wrap="nowrap">
                <IconUsers size={20} stroke={1.6} />
                <Text fw={700} size="lg">
                  Операторы
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Команда workspace, роли, статусы и быстрый переход к профилям.
              </Text>
            </Box>
          }
          trailing={
            canManage ? (
            <Button leftSection={<IconUserPlus size={16} stroke={1.5} />} onClick={openCreate}>
              Добавить оператора
            </Button>
            ) : null
          }
        />

        {error ? (
          <Alert color="red" variant="light" title="Ошибка">
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <PageCenterLoader label="Загрузка" minHeight={200} />
        ) : operators.length === 0 ? (
          <EmptyState
            icon={<IconUsers size={36} stroke={1.2} />}
            title="Нет операторов"
            description="Добавьте первого оператора."
            action={
              canManage ? (
                <Button leftSection={<IconUserPlus size={16} stroke={1.5} />} onClick={openCreate}>
                  Добавить
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Card withBorder padding="sm" radius="md">
            <Stack gap="sm">
              <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
                <Checkbox label="Только онлайн" checked={onlyOnline} onChange={(e) => setOnlyOnline(e.currentTarget.checked)} />
                <Checkbox
                  label="Показывать неактивных"
                  checked={showInactive}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked
                    setShowInactive(checked)
                    if (checked) setOnlyActive(false)
                  }}
                />
                <Checkbox
                  label="Только активные"
                  checked={onlyActive}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked
                    setOnlyActive(checked)
                    setShowInactive(!checked)
                  }}
                />
                <Checkbox
                  label="Только админы и операторы"
                  checked={onlyAdminOp}
                  onChange={(e) => setOnlyAdminOp(e.currentTarget.checked)}
                />
                <MultiSelect
                  label="Роль"
                  placeholder={roleFilter.length === 0 ? 'Все роли' : undefined}
                  data={roleOptions}
                  value={roleFilter}
                  onChange={setRoleFilter}
                  searchable
                  clearable
                  style={{ flex: '1 1 200px', minWidth: 180 }}
                />
                <TextInput
                  label="Поиск"
                  placeholder="Имя, email, Mattermost"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ flex: '1 1 220px', minWidth: 200 }}
                />
                <Select
                  label="Сортировка"
                  data={[
                    { value: 'name_asc', label: 'Имя: А → Я' },
                    { value: 'name_desc', label: 'Имя: Я → А' },
                  ]}
                  value={sortMode}
                  onChange={(value) => setSortMode((value || 'name_asc') as 'name_asc' | 'name_desc')}
                  allowDeselect={false}
                  checkIconPosition="right"
                  styles={{ root: { minWidth: 190 } }}
                />
              </Group>

              {filtered.length === 0 ? (
                <Text size="sm" c="dimmed" py="md" ta="center">
                  По фильтрам никого не найдено.
                </Text>
              ) : (
                <DataTableScroll>
                  <DataTable>
                    <DataThead>
                      <DataTr>
                        <DataTh>Оператор</DataTh>
                        <DataTh>Email</DataTh>
                        <DataTh>Логин</DataTh>
                        <DataTh>Mattermost</DataTh>
                        <DataTh>Присоединился</DataTh>
                        <DataTh>Роль</DataTh>
                        <DataTh>Статус</DataTh>
                        <DataTh style={{ width: 1 }}>Действия</DataTh>
                      </DataTr>
                    </DataThead>
                    <DataTbody>
                      {filtered.map((o) => {
                        const src = resolveOperatorAvatarSrc({
                          operator_name: [o.first_name, o.last_name].filter(Boolean).join(' ').trim() || o.email,
                          operator_email: o.email,
                          avatar_url: o.avatar_url,
                        })
                        const crKey = o.custom_role ? String(o.custom_role).toLowerCase() : ''
                        const hasCustom = !!(o.custom_role && customRoleMap.has(crKey))
                        const customLabel = hasCustom
                          ? customRoleMap.get(crKey)?.display_name || o.custom_role
                          : null
                        const isSelf = o.id === user?.id
                        return (
                          <DataTr key={o.id}>
                            <DataTd>
                              <Group gap="sm" wrap="nowrap" align="center">
                                <ShiftPresenceDot userId={o.id} size={7} />
                                <Avatar src={src} radius="xl" size={32}>
                                  {operatorInitials(o)}
                                </Avatar>
                                <Stack gap={2}>
                                  <Text
                                    size="sm"
                                    fw={500}
                                    style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                    onClick={() => openProfile(o)}
                                  >
                                    {[o.first_name, o.last_name].filter(Boolean).join(' ') || o.email}
                                  </Text>
                                  {o.is_online ? (
                                    <Badge size="xs" color="green" variant="light">
                                      online
                                    </Badge>
                                  ) : null}
                                </Stack>
                              </Group>
                            </DataTd>
                            <DataTd>
                              <Text size="sm" c="dimmed">
                                {o.email || '—'}
                              </Text>
                            </DataTd>
                            <DataTd>
                              <Text size="sm" c="dimmed">
                                {o.username || '—'}
                              </Text>
                            </DataTd>
                            <DataTd>
                              <Text size="sm" c="dimmed">
                                {o.mattermost_login || '—'}
                              </Text>
                            </DataTd>
                            <DataTd>
                              <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                                {formatDateTimeDot(o.created_at)}
                              </Text>
                            </DataTd>
                            <DataTd>
                              <Group gap={6} wrap="wrap">
                                {customLabel ? (
                                  <>
                                    <Badge variant="light" size="sm">
                                      {customLabel}
                                    </Badge>
                                    <Badge variant="outline" size="sm" color="gray">
                                      {workspaceRoleLabel(o.role)}
                                    </Badge>
                                  </>
                                ) : (
                                  <Badge variant="light" size="sm">
                                    {workspaceRoleLabel(o.role)}
                                  </Badge>
                                )}
                              </Group>
                            </DataTd>
                            <DataTd>
                              <Badge color={o.is_active !== false ? 'green' : 'gray'} variant="light" size="sm">
                                {o.is_active !== false ? 'Активен' : 'Неактивен'}
                              </Badge>
                            </DataTd>
                            <DataTd>
                              {canEditOperatorRecord ? (
                                <Group gap={4} wrap="nowrap" justify="flex-end">
                                  <Button
                                    size="compact-xs"
                                    variant="light"
                                    leftSection={<IconEdit size={14} stroke={1.5} />}
                                    onClick={() => openEdit(o)}
                                  >
                                    {canManage ? 'Изменить' : 'Email / Логин'}
                                  </Button>
                                  {canManage && !isSelf ? (
                                    <ActionIconBtn onClick={() => confirmDelete(o)} />
                                  ) : null}
                                </Group>
                              ) : null}
                            </DataTd>
                          </DataTr>
                        )
                      })}
                    </DataTbody>
                  </DataTable>
                </DataTableScroll>
              )}
            </Stack>
          </Card>
        )}
      </Stack>

      <AppModal
        opened={formOpen}
        onClose={() => setFormOpen(false)}
        title={
          editOp
            ? canManage
              ? 'Редактировать оператора'
              : 'Изменить email и логин оператора'
            : 'Добавить оператора'
        }
        size="md"
        footer={
          <>
            <ActionButton action="cancel" onClick={() => setFormOpen(false)} />
            <ActionButton
              action={editOp ? 'save' : 'add'}
              loading={saving}
              onClick={() => void submitForm()}
              label={editOp ? 'Сохранить' : 'Добавить'}
            />
          </>
        }
      >
        <Stack gap="sm">
          {!editOp ? (
            <>
              <TextInput
                label="Email"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextInput
                label="Пароль"
                required
                type="password"
                description="Минимум 6 символов"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextInput
                label="Логин"
                description="Короткое имя пользователя (username). Если не указать — будет сгенерирован из email."
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <Group grow>
                <TextInput label="Имя" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                <TextInput label="Фамилия" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Group>
              <Select
                label="Роль пространства"
                description="Для кастомных ролей права задаются в настройках роли"
                data={roleMatrix.map((r) => {
                  const key = String(r.role || '').toLowerCase()
                  const label = (r.display_name || workspaceRoleLabel(key)) + (r.is_custom ? ' (кастомная)' : '')
                  return { value: key, label }
                })}
                value={workspaceRole}
                onChange={(value) => setWorkspaceRole(value || 'operator')}
                searchable
                allowDeselect={false}
                checkIconPosition="right"
              />
              <Checkbox
                label="Пользователь активен"
                description="Неактивные не могут войти в систему"
                checked={isActive}
                onChange={(e) => setIsActive(e.currentTarget.checked)}
              />
            </>
          ) : null}
          {editOp && (canManage || canManageEmails) ? (
            <>
              <TextInput
                label="Email"
                required
                type="email"
                description={
                  canManage
                    ? 'После смены оператор входит только по новому адресу.'
                    : 'Можно изменить email и логин; остальные поля недоступны.'
                }
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextInput
                label="Логин"
                description="Короткое имя пользователя (username), отображается в таблице операторов."
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </>
          ) : null}
          {editOp && canManage ? (
            <>
              <Group grow>
                <TextInput label="Имя" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                <TextInput label="Фамилия" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Group>
              <TextInput
                label="Новый пароль"
                type="password"
                description="Оставьте пустым, если не меняете"
                value={passwordNew}
                onChange={(e) => setPasswordNew(e.target.value)}
              />
              <Select
                label="Роль пространства"
                description="Для кастомных ролей права задаются в настройках роли"
                data={roleMatrix.map((r) => {
                  const key = String(r.role || '').toLowerCase()
                  const label = (r.display_name || workspaceRoleLabel(key)) + (r.is_custom ? ' (кастомная)' : '')
                  return { value: key, label }
                })}
                value={workspaceRole}
                onChange={(value) => setWorkspaceRole(value || 'operator')}
                searchable
                allowDeselect={false}
                checkIconPosition="right"
              />
              <Checkbox
                label="Пользователь активен"
                description="Неактивные не могут войти в систему"
                checked={isActive}
                onChange={(e) => setIsActive(e.currentTarget.checked)}
              />
            </>
          ) : null}
        </Stack>
      </AppModal>

      <AppModal opened={profileOpen} onClose={() => setProfileOpen(false)} title="Профиль коллеги" size="sm">
        {profileOp ? (
          <Stack gap="xs">
            <Text size="sm">
              <Text span fw={600}>
                Имя:{' '}
              </Text>
              {[profileOp.first_name, profileOp.last_name].filter(Boolean).join(' ') || '—'}
            </Text>
            <Text size="sm">
              <Text span fw={600}>
                Email:{' '}
              </Text>
              {profileOp.email || '—'}
            </Text>
            <Text size="sm">
              <Text span fw={600}>
                Mattermost:{' '}
              </Text>
              {profileOp.mattermost_login || '—'}
            </Text>
            <Text size="sm">
              <Text span fw={600}>
                Роль:{' '}
              </Text>
              {profileCustom ? (
                <>
                  {profileCustom}{' '}
                  <Text span c="dimmed" size="sm">
                    (базовая: {workspaceRoleLabel(profileOp.role)})
                  </Text>
                </>
              ) : (
                workspaceRoleLabel(profileOp.role)
              )}
            </Text>
            <Text size="sm">
              <Text span fw={600}>
                Статус:{' '}
              </Text>
              {profileOp.is_active !== false ? 'Активен' : 'Неактивен'}
            </Text>
            <Text size="sm">
              <Text span fw={600}>
                Онлайн:{' '}
              </Text>
              {profileOp.is_online ? 'Да' : 'Нет'}
            </Text>
            {profileOp.phone ? (
              <Text size="sm">
                <Text span fw={600}>
                  Телефон:{' '}
                </Text>
                {profileOp.phone}
              </Text>
            ) : null}
            {profileOp.note ? (
              <Text size="sm">
                <Text span fw={600}>
                  Примечание:{' '}
                </Text>
                {profileOp.note}
              </Text>
            ) : null}
          </Stack>
        ) : null}
      </AppModal>

    </PageShell>
  )
}

function ActionIconBtn({ onClick }: { onClick: () => void }) {
  return (
    <Button
      size="compact-xs"
      color="red"
      variant="light"
      leftSection={<IconTrash size={14} stroke={1.5} />}
      onClick={onClick}
    >
      Удалить
    </Button>
  )
}
